import orderBy from 'lodash/orderBy';
import { format } from 'date-fns';
import utcToZonedDate from '@framework/date-helpers/utcToZonedDate';
import { MONTH_DAY_YEAR_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import keyResolver from '@framework/data-helpers/keyResolver';
import memoize from '@framework/data-helpers/memoize';
import { getRatingsMapping as getRatingsMappingHelper } from '@/creditflowresearch/ui/common/helpers';
import {
  getDealRoute,
  getIndustryRoute,
  getParentRoute,
  getSectorRoute,
} from '@/creditflowresearch/Router/RouteWithRedirect/getNewRoute';
import formatNumber from '@framework/data-helpers/formatters/formatNumber';
import ISector from '@/creditflowresearch/domain/vo/IDealSector';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { ICorporateDeal } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import IDeal, { IIndustry, IJointLead, ILead, ISubindustry } from '@/creditflowresearch/domain/vo/IDeal';
import { IDatetime } from '@framework/date-helpers/Datetime';
import type IDealCellData from '@/creditflowresearch/ui/components/deals/DealListTable/cells/types';
import { IDictionarySizeDescription } from '@/creditflowresearch/domain/vo/IDictionary';

export function getDate(dateObject: IDatetime): string {
  if (!dateObject) {
    return '';
  }
  const { timezone, dateTime } = dateObject;
  return format(utcToZonedDate(timezone.timeZone, new Date(dateTime)), MONTH_DAY_YEAR_FORMAT);
}

export function getSectorIndustryLink(deal: IDealCellData): { pathname: string, search: string } | string {
  let pathname = '';
  if (deal.dealType === 'industry') {
    if (!deal.parent.industry) {
      return '#';
    }
    pathname = getIndustryRoute(deal.parent.industry.name, deal.parent.subindustry.name);
  } else {
    pathname = getSectorRoute(deal.sector.name, deal.subsector.name);
  }

  return {
    pathname,
    search: `products=${ deal.product.abbreviation }&regions=${ deal.region.abbreviation }`,
  };
}

export function mapDealFields(deal, index: number, collectionLength: number): IDealCellData {
  deal.structuringLeads = getSortedLeads(deal.structuringLeads);
  deal.jointLeads = getSortedLeads(deal.jointLeads);

  // map sector/subsector OR industry/subindustry
  deal.dealTypeObject = getSectorIndustryByDeal(deal);
  deal.dealTypeSubObject = getSubsectorSubindustryByDeal(deal);

  deal.region = deal.country?.region;

  // map links
  deal.sectorIndustryLink = getSectorIndustryLink(deal);
  deal.parentLink = getParentRoute(deal.parent);
  deal.link = getDealRoute(deal);

  // If deal has only one currency - display it formatted
  if (Object.keys(deal.totalSize).length === 1) {
    // @ts-ignore
    const currency: {
      size: number,
      displayName: string,
    } = Object.values(deal.totalSize)[0];

    const size = formatNumber(currency.size, {
      symbolOnLeft: true,
      symbol: currency.displayName,
      spaceBetweenValueAndSymbol: true,
    });

    deal.totalSize = `(${ size }m)`;
  } else {
    deal.totalSize = '';
  }

  // Add isFirst and isLast properties
  deal.isFirst = index === 0;
  deal.isLast = index === collectionLength - 1;

  return deal;
}

export function mapCorporateDealFields(deal: ICorporateDeal, {
  regionsList,
  industriesListAll,
  getSizeDescription,
}: {
  regionsList: IRegion[],
  industriesListAll: IIndustry[],
  getSizeDescription: (id: string | null) => IDictionarySizeDescription | undefined,
}) {
  deal.industry = industriesListAll.find(({ id }) => id === deal.industryId);
  deal.subindustry = deal.industry
    ? deal.industry.subindustryList.find(({ id }) => id === deal.subindustryId)
    : null;
  deal.region = regionsList.find(({ id }) => id === deal.regionId) || regionsList[0];
  deal.link = getDealRoute(deal);
  deal.sizeDescription = getSizeDescription(deal.sizeDescriptionId);

  return deal;
}

function getSortedLeads <T extends ILead | IJointLead>(leads: T[]): T[] {
  if (leads?.length) {
    return orderBy(leads, (lead) => lead.name);
  }
  return leads;
}

function getSectorIndustryByDeal(deal: IDealCellData) : IIndustry | ISector | { name: string, abbreviation: string } {
  if (deal.dealType === 'sector') {
    return deal.sector;
  }

  return deal.parent.industry || {
    name: '',
    abbreviation: '',
  };
}

function getSubsectorSubindustryByDeal(deal: IDealCellData) : ISector | ISubindustry | { name: string } {
  if (deal.dealType === 'sector') {
    return deal.subsector;
  }

  return deal.parent.subindustry || {
    name: '',
  };
}

export const getRatingsMapping = memoize((deal: IDeal, ratingAgenciesMapping) => {
  if (!deal || Object.keys(ratingAgenciesMapping).length === 0) {
    return [];
  }

  let ratingAgenciesForRegion = ratingAgenciesMapping[deal.product.id][deal.country.region.id];

  return getRatingsMappingHelper(deal.tranches, ratingAgenciesForRegion);
}, keyResolver);
